import mediaBadgeStyles from 'css/src/components/common/media-badge.module.scss';

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { AssetContainerTypes, SlideBackgroundModelTypeEnum, SourceEnum } from 'js/src/client';
import { IconButton } from 'js/src/components/common/ui/Button';
import type { PlainDropdownItem } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import Tooltip, { Placement } from 'js/src/components/common/ui/Tooltip';
import { getCanUseAIMedia } from 'js/src/data/account';
import type { StoredAssetContainer } from 'js/src/data/media';
import {
    AI_MEDIA_SUB_TAB_OPTIONS,
    getHasVideoToVideoModels,
    readonlySlideBackgroundModelIsImageBackgroundModel,
    readonlySlideBackgroundModelIsVideoBackgroundModel,
} from 'js/src/data/media';
import {
    MAX_VIDEO_TO_VIDEO_INPUT_DURATION_MS,
    type TransformImageData,
    type TransformVideoData,
    useTransformImage,
    useTransformVideo,
    VIDEO_TO_VIDEO_INPUT_TOO_LONG_MESSAGE,
} from 'js/src/data/media/media-hooks';

enum AIMediaDropdownActions {
    EDIT_IMAGE = 'edit-image',
    EDIT_VIDEO = 'edit-video',
    CONVERT_TO_VIDEO = 'convert-to-video',
    SIMILAR_SEARCH = 'similar-search',
}

const AIMediaDropdownToggleProps = {
    className: `btn-icon btn-media ${mediaBadgeStyles['media-badge']} ${mediaBadgeStyles['smart-search']}`,
    icon: 'auto_awesome',
};

type BackgroundAssetAIMediaDropdownProps = {
    media: StoredAssetContainer;
    handleSimilarSearch: VoidFunction;
    source: SourceEnum;
};

export const BackgroundAssetAIMediaDropdown: React.FC<BackgroundAssetAIMediaDropdownProps> = ({
    media,
    handleSimilarSearch,
    source,
}) => {
    const background = media[AssetContainerTypes.BACKGROUND];
    const imageMedia =
        background && readonlySlideBackgroundModelIsImageBackgroundModel(background)
            ? background[SlideBackgroundModelTypeEnum.IMAGE]
            : null;
    const videoMedia =
        background && readonlySlideBackgroundModelIsVideoBackgroundModel(background)
            ? background[SlideBackgroundModelTypeEnum.VIDEO]
            : null;
    const isImage = imageMedia !== null;
    const isVideo = videoMedia !== null;

    const [menuIsOpen, setMenuIsOpen] = React.useState(false);

    const menuAfterOpen = React.useCallback(() => {
        setMenuIsOpen(true);
    }, []);

    const menuAfterClose = React.useCallback(() => {
        setMenuIsOpen(false);
    }, []);

    const canUseAIMedia = useSelector(getCanUseAIMedia);
    const hasVideoToVideoModels = useSelector(getHasVideoToVideoModels);
    const canUseVideoToVideo = hasVideoToVideoModels;

    const transformImageData: TransformImageData | null = React.useMemo(() => {
        if (!imageMedia) {
            return null;
        }
        const imageUrl = imageMedia.thumbnail_url;
        const uuid = imageMedia.uuid;
        if (!uuid) {
            return null;
        }
        const name = imageMedia.title || '';

        return {
            imageUrl,
            uuid,
            name,
        };
    }, [imageMedia]);

    const transformVideoData: TransformVideoData | null = React.useMemo(() => {
        if (!videoMedia) {
            return null;
        }
        const videoUrl = videoMedia.thumbnail_url;
        const uuid = videoMedia.uuid;
        if (!uuid) {
            return null;
        }
        const name = videoMedia.title || '';
        const durationMs = videoMedia.duration || 0;

        return {
            videoUrl,
            uuid,
            name,
            durationMs,
        };
    }, [videoMedia]);

    const { onTransformImage } = useTransformImage({
        transformImageData,
        assetSource: source,
        analyticsMeta: {
            eventName: 'Background asset AI media dropdown clicked',
            location: 'media tab',
        },
    });

    const { onTransformVideo } = useTransformVideo({
        transformVideoData,
        assetSource: source,
        analyticsMeta: {
            eventName: 'Background asset AI media dropdown clicked',
            location: 'media tab',
        },
    });

    const onDropdownSelect = React.useCallback(
        (onSelectData: { type: AIMediaDropdownActions }) => {
            switch (onSelectData.type) {
                case AIMediaDropdownActions.EDIT_IMAGE: {
                    onTransformImage(AI_MEDIA_SUB_TAB_OPTIONS.IMAGE);
                    break;
                }
                case AIMediaDropdownActions.EDIT_VIDEO: {
                    onTransformVideo();
                    break;
                }
                case AIMediaDropdownActions.CONVERT_TO_VIDEO: {
                    onTransformImage(AI_MEDIA_SUB_TAB_OPTIONS.VIDEO);
                    break;
                }
                case AIMediaDropdownActions.SIMILAR_SEARCH: {
                    handleSimilarSearch();
                    break;
                }
                default: {
                    throw Error('Unknown action type from BackgroundAssetAIMediaDropdown');
                }
            }
        },
        [handleSimilarSearch, onTransformImage, onTransformVideo],
    );

    const aiMediaDropdownItems = useMemo(() => {
        const items: PlainDropdownItem<{ type: AIMediaDropdownActions }>[] = [];

        if (![SourceEnum.SOURCE_USER, SourceEnum.SOURCE_AI_GENERATED].includes(source)) {
            items.push({
                id: 'similar-search',
                text: 'Suggest media',
                onSelectData: { type: AIMediaDropdownActions.SIMILAR_SEARCH },
                icon: 'smart_search',
            });
        }

        if (canUseAIMedia && isImage) {
            items.push({
                id: 'edit-image',
                text: 'Edit image',
                onSelectData: { type: AIMediaDropdownActions.EDIT_IMAGE },
                icon: 'smart_edit',
            });

            items.push({
                id: 'convert-to-video',
                text: 'Convert to video',
                onSelectData: { type: AIMediaDropdownActions.CONVERT_TO_VIDEO },
                icon: 'smart_media',
            });
        }

        const isVideoEditableSource = [
            SourceEnum.SOURCE_USER,
            SourceEnum.SOURCE_AI_GENERATED,
        ].includes(source);

        if (canUseAIMedia && canUseVideoToVideo && isVideo && isVideoEditableSource) {
            const isSourceVideoTooLong =
                transformVideoData !== null &&
                transformVideoData.durationMs > MAX_VIDEO_TO_VIDEO_INPUT_DURATION_MS;
            items.push({
                id: 'edit-video',
                text: 'Edit video',
                onSelectData: { type: AIMediaDropdownActions.EDIT_VIDEO },
                icon: 'smart_edit',
                disabled: isSourceVideoTooLong,
                tooltipTitle: isSourceVideoTooLong
                    ? VIDEO_TO_VIDEO_INPUT_TOO_LONG_MESSAGE
                    : undefined,
                tooltipPlacement: Placement.RIGHT,
            });
        }

        return items;
    }, [canUseAIMedia, canUseVideoToVideo, isImage, isVideo, source, transformVideoData]);

    if (!aiMediaDropdownItems.length) {
        return null;
    }
    return (
        <Tooltip
            title="AI media tools"
            trigger="hover"
            placement={Placement.TOP}
            delay={{ show: 500, hide: 0 }}
        >
            <div
                className={classNames(
                    'position-absolute top-0 end-0 m-l5-4 hide-while-dragging hidden-soft',
                    { 'menu-is-open': menuIsOpen },
                )}
                data-testid="background-asset-ai-media-dropdown"
            >
                <Dropdown
                    toggleProps={AIMediaDropdownToggleProps}
                    ToggleComponent={IconButton}
                    items={aiMediaDropdownItems}
                    onItemSelect={onDropdownSelect}
                    myMenuPath="background-asset-ai-media-dropdown"
                    className="background-asset-ai-media-dropdown"
                    afterOpen={menuAfterOpen}
                    afterClose={menuAfterClose}
                    breakOverflow
                />
            </div>
        </Tooltip>
    );
};
